import React, { useEffect, useState } from 'react';
import { getEarningsApi } from '../../api/revenue';
import './RevenueDashboard.scss';
import DummyUser from '../../assets/images/avatar.png';

export const RevenueDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('communities');
  const [activeTab, setActiveTab] = useState('Active');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [data, setData] = useState({
    totalEarnings: "0.00",
    totalCompleted: "0.00",
    totalScheduled: "0.00",
    totalPending: "0.00",
    ownerEarnings: "0.00",
    payouts: []
  });

  const [communityStats, setCommunityStats] = useState({
    active: 0,
    cancelling: 0,
    churned: 0,
    banned: 0
  });

  const [communityMembers, setCommunityMembers] = useState({
    active: [],
    cancelling: [],
    churned: [],
    banned: []
  });

  const [privateStats, setPrivateStats] = useState({
    totalStudents: 0,
    upcomingClasses: 0,
    completedClasses: 0,
    averageRating: 0
  });
  const [privateStudents, setPrivateStudents] = useState([]);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        setLoading(true);
        const res = await getEarningsApi({ type: viewType, page: currentPage });
        if (res && res.status === 200) {
          setData({
            totalEarnings: res.totalEarnings || "0.00",
            totalCompleted: res.totalCompleted || "0.00",
            totalScheduled: res.totalScheduled || "0.00",
            totalPending: res.summary?.pending || "0.00",
            ownerEarnings: res.ownerEarnings || "0.00",
            payouts: res.data || []
          });

          if (res.communityStats) {
            setCommunityStats({
              active: res.communityStats.active || 0,
              cancelling: res.communityStats.cancelling || 0,
              churned: res.communityStats.churned || 0,
              banned: res.communityStats.banned || 0
            });
          }

          if (res.communityMembers) {
            setCommunityMembers({
              active: res.communityMembers.active || [],
              cancelling: res.communityMembers.cancelling || [],
              churned: res.communityMembers.churned || [],
              banned: res.communityMembers.banned || []
            });
          }

          if (res.privateStats) {
            setPrivateStats(res.privateStats);
          }
          if (res.privateStudents) {
            setPrivateStudents(res.privateStudents);
          }

          if (res.pagination) {
            setCurrentPage(res.pagination.currentPage || 1);
            setTotalPages(res.pagination.totalPages || 1);
          }
        }
      } catch (err) {
        console.error("Failed to fetch earnings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [viewType, currentPage]);

  const handleViewTypeChange = (newType) => {
    setViewType(newType);
    setCurrentPage(1);
  };

  const tabs = [
    { label: 'Active', count: communityStats.active },
    { label: 'Cancelling', count: communityStats.cancelling },
    { label: 'Churned', count: communityStats.churned },
    { label: 'Banned', count: communityStats.banned }
  ];

  return (
    <div className="revenue-dashboard">
      <div className="dashboard-container">

        {/* Page Title */}
        <div className="mb-4">
          <h2 className="fw-bold text-dark">Revenue Dashboard</h2>
          <p className="text-muted">Track your community earnings, class payouts, and student subscriptions.</p>
        </div>

        {/* Top Overview Cards */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
              <span className="text-muted text-xs text-uppercase tracking-wider">Total Earnings</span>
              <h3 className="fw-black text-dark mt-2 mb-0">${parseFloat(data.totalEarnings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
              <span className="text-muted text-xs text-uppercase tracking-wider">Owner Net Earnings</span>
              <h3 className="fw-black text-dark mt-2 mb-0">${parseFloat(data.ownerEarnings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
              <span className="text-muted text-xs text-uppercase tracking-wider">Completed Payouts</span>
              <h3 className="fw-black text-success mt-2 mb-0">${parseFloat(data.totalCompleted).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
              <span className="text-muted text-xs text-uppercase tracking-wider">Scheduled & Pending</span>
              <h3 className="fw-black text-warning mt-2 mb-0">${parseFloat(data.totalScheduled).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${parseFloat(data.totalPending).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
        </div>
        <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-4">
          <h5 className="fw-bold text-dark mb-4">Earnings & Payouts Transactions</h5>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr className="text-muted text-uppercase text-xs border-bottom">
                  <th>Date</th>
                  <th>Reference ID</th>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Gross Amount</th>
                  <th>Fee</th>
                  <th>Net Earnings</th>
                  <th>Status</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>
                      Loading transactions...
                    </td>
                  </tr>
                ) : data.payouts.length > 0 ? (
                  data.payouts.map((payout, index) => (
                    <tr key={payout.payoutId || index}>
                      <td className="text-nowrap">{payout.date || (payout.createdAt ? new Date(payout.createdAt).toLocaleDateString('en-GB') : '')}</td>
                      <td className="text-muted font-monospace text-xs">{payout.transactionId || payout.payoutId || '-'}</td>
                      <td>
                        <span className="fw-bold text-dark d-block">{payout.typeName || 'Class/Course Payout'}</span>
                        {payout.description && <span className="text-muted text-xs d-block text-truncate" style={{ maxWidth: '250px' }} title={payout.description}>{payout.description}</span>}
                      </td>
                      <td>
                        <span className={`badge rounded-pill text-xs px-2.5 py-1 ${payout.type === 'course' ? 'bg-primary bg-opacity-10 text-primary' : 'bg-success bg-opacity-10 text-success'}`}>
                          {payout.type || 'class'}
                        </span>
                      </td>
                      <td className="fw-semibold text-dark">${parseFloat(payout.totalAmount || payout.transactionAmount || 0).toFixed(2)}</td>
                      <td className="text-danger">-${parseFloat(payout.platformFee || 0).toFixed(2)}</td>
                      <td className="text-success fw-bold">${parseFloat(payout.recipientAmount || 0).toFixed(2)}</td>
                      <td>
                        <span className={`badge rounded-pill text-xs px-2.5 py-1 ${payout.status === 'completed' || payout.status === 'succeeded' ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {payout.status || 'pending'}
                        </span>
                      </td>
                      <td className="text-uppercase text-muted text-xs">{payout.payoutMethod || payout.paymentMethod || 'paypal'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      No recent transactions available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left me-1"></i> Previous
              </button>
              <span className="text-muted small">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <i className="bi bi-chevron-right ms-1"></i>
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
